import { useTranslation } from '@govard/i18n-core/react';
// «Забыли пароль»: запрос письма со ссылкой на смену.
//
// Ответ сервера одинаков и для существующего адреса, и для неизвестного —
// экран отражает это честно: он не сообщает, зарегистрирован человек или нет.
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';

import { requestPasswordReset } from '../api/account.client.js';
import { AuthMark } from '../components/AuthMark.js';
import { LanguageSwitcher } from '../../../components/layout/LanguageSwitcher.js';

const { Title, Text } = Typography;

interface FormValues {
  email: string;
}

export function ForgotPasswordPage(): ReactElement {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(values: FormValues): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await requestPasswordReset(values.email);
      setSent(true);
    } catch (err) {
      setError(
        err instanceof Error && err.message.includes('rate')
          ? t('crm.auth.forgot.rate_limited')
          : t('crm.auth.forgot.send_error'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="relative flex min-h-screen items-center justify-center p-6"
      style={{ backgroundColor: 'var(--ant-color-bg-layout)' }}
    >
      <div className="absolute right-6 top-6" data-testid="public-language-switcher-position">
        <LanguageSwitcher />
      </div>
      <Card style={{ maxWidth: 440, width: '100%' }}>
        <div className="mb-4 flex items-center gap-3">
          <AuthMark />
          <Title level={4} style={{ margin: 0 }}>
            {t('crm.auth.forgot.title')}
          </Title>
        </div>

        {sent ? (
          <div className="flex flex-col gap-3">
            <Alert
              type="success"
              showIcon
              title={t('crm.auth.forgot.sent_title')}
              description={t('crm.auth.forgot.sent_description')}
            />
            <Link to="/login">
              <Button>{t('crm.auth.forgot.back_to_login')}</Button>
            </Link>
          </div>
        ) : (
          <Form<FormValues> form={form} layout="vertical" onFinish={(v) => void handleSubmit(v)}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('crm.auth.forgot.description')}
            </Text>

            {error ? <Alert type="error" showIcon className="mt-3" title={error} /> : null}

            <Form.Item
              name="email"
              label="Email"
              className="mt-3"
              rules={[
                { required: true, message: t('crm.auth.email_required') },
                { type: 'email', message: t('crm.auth.email_invalid') },
              ]}
            >
              <Input autoFocus placeholder="you@example.com" maxLength={320} />
            </Form.Item>

            <div className="flex items-center gap-3">
              <Button type="primary" htmlType="submit" loading={submitting}>
                {t('crm.auth.forgot.submit')}
              </Button>
              <Link to="/login">
                <Button type="link">{t('crm.auth.forgot.remembered')}</Button>
              </Link>
            </div>
          </Form>
        )}
      </Card>
    </main>
  );
}
